import { Alert, Button, Checkbox, Field, Modal } from '@catering/ui';
import { type FormEvent, type JSX, useState } from 'react';

import { NEVER_CUSTOM_ROLE_PERMISSIONS, type Permission, type Role } from '../../../../roles/api';
import { groupPermissionsByResource } from '../../../../roles/permission-groups';

/** `apps/api` `roles.dto.ts` `roleCodeSchema`: starts with a letter, then letters/digits/underscore. */
const ROLE_CODE_PATTERN = /^[A-Z][A-Z0-9_]*$/;

export interface RoleFormSubmitValues {
  code?: string;
  name: string;
  description?: string;
  permissionCodes: string[];
}

export interface RoleFormModalProps {
  mode: 'create' | 'edit';
  /** Required in edit mode: precharges `name`/`description`/`permissionCodes`, and locks `code`. */
  role?: Role;
  /** The full permission catalogue — the three forbidden codes are filtered out here, not by the caller. */
  permissions: Permission[];
  actorPermissionCodes: string[];
  submitting: boolean;
  formError?: string;
  onCancel: () => void;
  onSubmit: (values: RoleFormSubmitValues) => Promise<void>;
}

/**
 * Shared between create and edit (design.md §9.5, "compartida entre
 * crear/editar"): `role` undefined means create. `role.manage`,
 * `user.impersonate` and `fiscal.config` never appear as an option, even
 * for `SUPER_ADMIN` — not disabled, absent (`custom-roles/spec.md`,
 * "Permisos prohibidos en roles personalizados").
 */
export function RoleFormModal({
  mode,
  role,
  permissions,
  actorPermissionCodes,
  submitting,
  formError,
  onCancel,
  onSubmit,
}: RoleFormModalProps): JSX.Element {
  const isEdit = mode === 'edit';
  const [code, setCode] = useState(role?.code ?? '');
  const [name, setName] = useState(role?.name ?? '');
  const [description, setDescription] = useState(role?.description ?? '');
  const [permissionCodes, setPermissionCodes] = useState<string[]>(role?.permissions ?? []);
  const [codeError, setCodeError] = useState<string | undefined>(undefined);

  const actorSet = new Set(actorPermissionCodes);
  const assignable = permissions.filter(
    (permission) => !NEVER_CUSTOM_ROLE_PERMISSIONS.includes(permission.code as never),
  );
  const groups = groupPermissionsByResource(assignable);

  function togglePermission(permissionCode: string): void {
    setPermissionCodes((current) =>
      current.includes(permissionCode)
        ? current.filter((code) => code !== permissionCode)
        : [...current, permissionCode],
    );
  }

  function handleSubmit(event: FormEvent): void {
    event.preventDefault();

    if (!isEdit && !ROLE_CODE_PATTERN.test(code)) {
      setCodeError(
        'El código solo puede tener mayúsculas, números y guion bajo (debe empezar con una letra).',
      );
      return;
    }

    setCodeError(undefined);

    void onSubmit({
      ...(isEdit ? {} : { code }),
      name,
      ...(description === '' ? {} : { description }),
      permissionCodes,
    });
  }

  return (
    <Modal
      open
      onClose={onCancel}
      title={isEdit ? 'Editar rol' : 'Nuevo rol'}
      className="max-w-2xl"
    >
      <form onSubmit={handleSubmit} className="flex flex-col gap-4" noValidate>
        {formError !== undefined ? <Alert tone="danger">{formError}</Alert> : null}

        <Field
          label="Código"
          name="code"
          required
          disabled={isEdit}
          value={code}
          onChange={(event) => setCode(event.target.value.toUpperCase())}
          {...(codeError === undefined ? {} : { error: codeError })}
        />

        <Field
          label="Nombre"
          name="name"
          required
          value={name}
          onChange={(event) => setName(event.target.value)}
        />

        <Field
          label="Descripción"
          name="description"
          value={description}
          onChange={(event) => setDescription(event.target.value)}
        />

        <fieldset className="flex flex-col gap-4">
          <legend className="text-sm font-medium text-slate-900">Permisos</legend>
          <p className="text-xs text-slate-500">
            Los permisos para gestionar roles, operar como otro usuario y configurar los datos
            fiscales quedan reservados a los roles del sistema: no se pueden asignar a un rol
            personalizado.
          </p>

          {groups.map((group) => (
            <div key={group.resource} className="flex flex-col gap-2">
              <p className="text-sm font-semibold text-slate-700">{group.label}</p>
              <div className="grid grid-cols-1 gap-2 pl-2 sm:grid-cols-2">
                {group.permissions.map((permission) => {
                  const held = actorSet.has(permission.code);

                  return (
                    <Checkbox
                      key={permission.code}
                      label={permission.description}
                      checked={permissionCodes.includes(permission.code)}
                      onChange={() => togglePermission(permission.code)}
                      disabled={!held}
                      title={held ? undefined : 'No tenés este permiso'}
                    />
                  );
                })}
              </div>
            </div>
          ))}
        </fieldset>

        <div className="mt-2 flex items-center gap-3">
          <Button type="submit" loading={submitting}>
            {isEdit ? 'Guardar cambios' : 'Crear rol'}
          </Button>
          <Button type="button" variant="secondary" onClick={onCancel}>
            Cancelar
          </Button>
        </div>
      </form>
    </Modal>
  );
}
